/**
 * Condensed DIN 276 property editor — the same "Attribut | Wert" grid used in
 * the part hierarchy (Teilehierarchie): label in front of the input, unit
 * behind. Controlled: edits call `onChange` with the next properties; the
 * caller supplies `onPersist` for immediate Select/boolean saves and
 * blur-triggered text/number saves.
 */
import "../administration/din276/din276Tree.css";
import "./assetPartHierarchy.css";

import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Building2, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { userAPI } from "../UserAPI";
import { useModalContext } from "../components/ModalProvider";
import {
  buildTree,
  expandUnit,
  type DinAttr,
  type DinTree,
} from "../administration/din276/din276Tree";
import { isStructure, valueAt, withValueAt } from "./dinValues";
import DerivedValue from "./DerivedValue";

type InputKind = "enum" | "boolean" | "number" | "date" | "text";

function inputKind(attr: DinAttr): InputKind {
  if (attr.enum && attr.enum.length > 0) return "enum";
  if (attr.type === "Boolean") return "boolean";
  if (attr.type === "Dezimalzahl" || attr.type === "Ganzzahl" || attr.measured) return "number";
  if (attr.type === "Datum") return "date";
  return "text";
}

/** Parsed number, empty string when cleared, or null for partial/invalid input. */
function parseNumericFieldValue(raw: string): number | "" | null {
  if (raw === "") return "";
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

const QUALITY_STATE_OPTIONS = [
  "notAcceptable",
  "bad",
  "acceptable",
  "good",
  "veryGood",
] as const;

interface Properties {
  /** DIN 276 category code (asset.schemaCategory). */
  typeCode?: string;
  properties: Record<string, any>;
  /** Called with the next properties on every edit. */
  onChange: (properties: Record<string, any>) => void;
  /** Optional: persist hook — immediate for Select/boolean, on blur for text/number. */
  onPersist?: (properties: Record<string, any>) => void;
  /** Asset-only: show the quality state dropdown (not part of DIN 276 metadata). */
  showQualityState?: boolean;
  /** Product: also list non-DIN276 (captured) properties below the schema fields,
   *  each deletable with a confirmation dialog. Read-only values. */
  showExtras?: boolean;
  /** Lay the attributes out in two attribute/value column pairs when the container is wide enough. */
  wide?: boolean;
  /** Read-only: hide the per-extra delete (bin) affordance (inputs are disabled by the caller). */
  readOnly?: boolean;
  /** Additional fields to show before DIN 276 attributes (e.g., product dimensions). */
  additionalFields?: DinAttr[];
}

export default function DinPropertySheet({
  typeCode,
  properties,
  onChange,
  onPersist,
  showQualityState = false,
  showExtras = false,
  wide = false,
  readOnly = false,
  additionalFields = [],
}: Properties) {
  const { t } = useTranslation();
  const { showDeleteDialog } = (useModalContext() as any) || {};
  const [raw, setRaw] = useState<any[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch full metadata (without filtering) — schemaCategory may be stored as
        // a code or a label (e.g. from crawler imports), and we need pending nodes.
        const meta = await userAPI.getDIN276MetadataFull();
        if (!cancelled) setRaw(Array.isArray(meta) ? meta : []);
      } catch {
        /* metadata is best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tree: DinTree | null = useMemo(() => (raw ? buildTree(raw) : null), [raw]);
  
  // Lookup by code first, then fallback to case-insensitive label match
  // (schemaCategory may be a code or a human-readable name from imports).
  const resolveNode = (code: string | undefined): DinAttr[] => {
    if (!code || !tree) return [];
    const byCode = tree.byCode[String(code)];
    if (byCode) return byCode.attrs;
    const needle = String(code).trim().toLowerCase();
    for (const node of Object.values(tree.byCode)) {
      if (node.label.toLowerCase() === needle) return node.attrs;
    }
    return [];
  };
  
  const attrs: DinAttr[] = resolveNode(typeCode);

  // Non-DIN276 captured attributes (capture-all): everything not covered by the
  // category's DIN 276 fields (or the asset quality state or additional fields).
  const attrNames = new Set(attrs.map((a) => a.name));
  const additionalNames = new Set(additionalFields.map((a) => a.name));
  const extraKeys = showExtras
    ? Object.keys(properties || {}).filter(
        (k) => !attrNames.has(k) && !additionalNames.has(k) && k !== "qualityState",
      )
    : [];
  // Render a nested (matrix) value as a compact table: a 2-D grid when the inner
  // values are objects (rows × union-of-inner-keys), else a 1-D name/value table.
  const cell = { border: "1px solid #e0e0e0", padding: "1px 6px", textAlign: "left" as const };
  const renderMatrix = (v: any) => {
    const entries = Object.entries(v as Record<string, any>);
    const twoD = entries.some(([, val]) => val && typeof val === "object");
    if (twoD) {
      const cols = Array.from(
        new Set(
          entries.flatMap(([, val]) =>
            val && typeof val === "object" ? Object.keys(val) : [],
          ),
        ),
      );
      return (
        <table style={{ borderCollapse: "collapse", fontSize: "11px", flex: 1 }}>
          <thead>
            <tr>
              <th style={cell} />
              {cols.map((c) => (
                <th style={cell} key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map(([r, val]) => (
              <tr key={r}>
                <td style={{ ...cell, fontWeight: 600 }}>{r}</td>
                {cols.map((c) => (
                  <td style={cell} key={c}>
                    {val && typeof val === "object" && val[c] != null ? String(val[c]) : ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return (
      <table style={{ borderCollapse: "collapse", fontSize: "11px", flex: 1 }}>
        <tbody>
          {entries.map(([r, val]) => (
            <tr key={r}>
              <td style={{ ...cell, fontWeight: 600 }}>{r}</td>
              <td style={cell}>{val == null ? "" : String(val)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };
  const deleteExtra = (k: string) => {
    const doDelete = () => {
      const next = { ...(properties || {}) };
      delete next[k];
      onChange(next);
      if (onPersist) onPersist(next);
    };
    if (showDeleteDialog) showDeleteDialog("Merkmal löschen", k, doDelete);
    else if (typeof window === "undefined" || window.confirm(`"${k}" löschen?`)) doDelete();
  };

  if (attrs.length === 0 && !showQualityState && extraKeys.length === 0 && additionalFields.length === 0) return null;

  // A path is [attribute] for a plain attribute and [structure, …, field] inside a structure.
  const set = (path: string[], value: any) => onChange(withValueAt(properties, path, value));

  const persistAt = (path: string[], value: any) => {
    if (onPersist) onPersist(withValueAt(properties, path, value));
  };

  const setAndPersist = (path: string[], value: any) => {
    const next = withValueAt(properties, path, value);
    onChange(next);
    if (onPersist) onPersist(next);
  };

  const renderInput = (attr: DinAttr, path: string[] = [attr.name]) => {
    if (attr.derived) return <DerivedValue attr={attr} properties={properties} />;
    const kind = inputKind(attr);
    const val = valueAt(properties, path);

    if (kind === "enum") {
      return (
        <Select
          value={val != null && val !== "" ? String(val) : undefined}
          onValueChange={(v) => setAndPersist(path, v)}
        >
          <SelectTrigger className="aph-input">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {attr.enum.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (kind === "boolean") {
      const cur = val === true ? "true" : val === false ? "false" : undefined;
      return (
        <Select value={cur} onValueChange={(v) => setAndPersist(path, v === "true")}>
          <SelectTrigger className="aph-input">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">{t("global.yes", "Ja")}</SelectItem>
            <SelectItem value="false">{t("global.no", "Nein")}</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (kind === "date") {
      // Stored as "YYYY-MM-DD"; a longer ISO timestamp from an import shows its day.
      return (
        <Input
          className="aph-input"
          type="date"
          value={val != null ? String(val).slice(0, 10) : ""}
          onChange={(e) => set(path, e.target.value)}
          onBlur={(e) => persistAt(path, e.target.value)}
        />
      );
    }

    return (
      <>
        <Input
          className={`aph-input${kind === "number" ? " aph-input-num" : ""}`}
          type={kind === "number" ? "number" : "text"}
          step={attr.type === "Ganzzahl" ? 1 : undefined}
          value={val != null ? String(val) : ""}
          onChange={(e) => {
            if (kind === "number") {
              const parsed = parseNumericFieldValue(e.target.value);
              if (parsed !== null) set(path, parsed);
              return;
            }
            set(path, e.target.value);
          }}
          onBlur={(e) => {
            if (!onPersist) return;
            if (kind === "number") {
              const parsed = parseNumericFieldValue(e.target.value);
              const value = parsed === null ? (val ?? "") : parsed;
              if (parsed === null) onChange(withValueAt(properties, path, value));
              persistAt(path, value);
              return;
            }
            persistAt(path, e.target.value);
          }}
        />
        {kind === "number" && attr.unit && (
          <span className="aph-unit-suffix" title={expandUnit(attr.unit)}>
            {attr.unit}
          </span>
        )}
      </>
    );
  };

  const ownChip = (
    <span
      className="din276tree-norm norm-tenant din276tree-own"
      title={t("din276.ownAttributeHint", "Vom Kunden ergänzt (Betreiberpflichten → Datenmodell)")}
    >
      <Building2 size={11} aria-hidden />
      {t("din276.ownAttribute", "eigen")}
    </span>
  );

  // One attribute row; a structure is a heading row with its fields indented below it.
  const renderAttr = (attr: DinAttr, path: string[], key: string, depth = 0): React.ReactNode => {
    const name = (
      <div
        className="din276tree-sheet-nm"
        style={depth > 0 ? { paddingLeft: depth * 14 } : undefined}
        title={attr.name}
      >
        {depth > 0 && <span className="din276tree-mute">↳ </span>}
        {attr.name}
        {attr.own && depth === 0 && ownChip}
      </div>
    );
    if (isStructure(attr)) {
      return (
        <Fragment key={key}>
          {name}
          <div className="aph-val" />
          {(attr.children ?? []).map((field, i) =>
            renderAttr(field, [...path, field.name], `${key}/${field.name}-${i}`, depth + 1),
          )}
        </Fragment>
      );
    }
    return (
      <Fragment key={key}>
        {name}
        <div className="aph-val">{renderInput(attr, path)}</div>
      </Fragment>
    );
  };

  const qualityStateLabel = (key: string) => {
    switch (key) {
      case "notAcceptable":
        return t("DIN276AssetProperties.notAcceptable");
      case "bad":
        return t("DIN276AssetProperties.bad");
      case "acceptable":
        return t("DIN276AssetProperties.acceptable");
      case "good":
        return t("DIN276AssetProperties.good");
      case "veryGood":
        return t("DIN276AssetProperties.veryGood");
      default:
        return key;
    }
  };

  const sheet = (
    <div className={wide ? "aph-sheet aph-sheet-wide" : "aph-sheet"}>
      <div className="din276tree-sheet-h">{t("partHierarchy.attribute", "Attribut")}</div>
      <div className="din276tree-sheet-h">{t("partHierarchy.value", "Wert")}</div>
      {wide ? (
        <>
          <div className="din276tree-sheet-h aph-sheet-h2">{t("partHierarchy.attribute", "Attribut")}</div>
          <div className="din276tree-sheet-h aph-sheet-h2">{t("partHierarchy.value", "Wert")}</div>
        </>
      ) : null}
      {showQualityState ? (
        <>
          <div className="din276tree-sheet-nm">
            {t("DIN276AssetProperties.qualityState", "Quality state")}
          </div>
          <div className="aph-val">
            <Select
              value={
                properties?.qualityState != null && properties.qualityState !== ""
                  ? String(properties.qualityState)
                  : undefined
              }
              onValueChange={(v) => setAndPersist(["qualityState"], v)}
            >
              <SelectTrigger className="aph-input">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {QUALITY_STATE_OPTIONS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {qualityStateLabel(key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      ) : null}
      {additionalFields.map((a, i) => (
        <Fragment key={`additional-${a.name}-${i}`}>
          <div className="din276tree-sheet-nm">{a.name}</div>
          <div className="aph-val">{renderInput(a)}</div>
        </Fragment>
      ))}
      {attrs.map((a, i) => renderAttr(a, [a.name], `${a.name}-${i}`))}
      {extraKeys.map((k) => {
        const v = properties?.[k];
        const isObj = v != null && typeof v === "object";
        return (
        <Fragment key={`extra-${k}`}>
          <div
            className="din276tree-sheet-nm"
            title={t("din276.nonDin276", "nicht DIN 276")}
          >
            {k}
          </div>
          <div
            className="aph-val"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            {isObj ? (
              // nested/matrix value — render as a compact nested table (read-only)
              renderMatrix(v)
            ) : (
              <Input
                className="aph-input"
                style={{ flex: 1 }}
                value={v != null ? String(v) : ""}
                onChange={(e) => set([k], e.target.value)}
                onBlur={(e) => {
                  if (onPersist)
                    onPersist({ ...(properties || {}), [k]: e.target.value });
                }}
              />
            )}
            {!readOnly && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                title={t("din276.deleteAttribute", "Merkmal löschen")}
                aria-label={t("din276.deleteAttribute", "Merkmal löschen")}
                onClick={() => deleteExtra(k)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </Fragment>
        );
      })}
    </div>
  );
  return wide ? <div className="aph-sheet-wrap">{sheet}</div> : sheet;
}
